<template>
  <simple-layout>
    <basic-card>
      <template slot="header">Processes overview</template>
      <collapsible-content>
        <template slot="header">Filters</template>
        <form-wrapper>
          <form-select-input caption="Load query" v-model="processQuery" :options="processQueryOptions"/>
          <form-text-input caption="Query name" v-model="processQueryName"/>
        </form-wrapper>

        <filter-form :filter-definition="processQueryDefinition" v-model="query"/>
        <simple-horizontal-layout>
          <empty/>
          <empty/>
          <empty/>
          <simple-button @press="clearFilter">Clear filter</simple-button>
          <simple-button @press="saveFilter">Save filter</simple-button>
          <simple-button @press="pageChanged">Filter</simple-button>
        </simple-horizontal-layout>
      </collapsible-content>
      <collapsible-content>
        <template slot="header">
          Content
        </template>
      </collapsible-content>
      <data-table
          :configuration="importProcessTableConfig"
          :data="sortedProcesses"
          @focusedChanged="handleFocusedChanged"
          component-id="processes-list"
          :pagination="listPagination"
          @pageChanged="pageChanged"
      >
        <basic-card v-if="focusedProcess != null">
          <template slot="header">{{ focusedProcess._id }}</template>
          <data-table
              :configuration="statusUpdatesTableConfig"
              :data="focusedProcess.statusUpdates"
              component-id="status-updates-table-config"/>
        </basic-card>
      </data-table>
    </basic-card>
  </simple-layout>
</template>

<script>
import SimpleLayout from "../components/layout/SimpleLayout";
import BasicCard from "../components/layout/BasicCard";
import DataTable from "../components/table/DataTable";
import {mapGetters} from "vuex";
import FilterForm from "../components/filters/FilterForm";
import FormTextInput from "../components/form/FormTextInput";
import FormSelectInput from "../components/form/FormSelectInput";
import QueryUtils from "../utils/QueryUtils";
import CollapsibleContent from "../components/CollapsibleContent";
import FormWrapper from "../components/form/FormWrapper";
import SimpleButton from "../components/button/SimpleButton";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import Empty from "../components/layout/Empty";

export default {
  name: "ProcessesView",
  components: {
    Empty,
    SimpleHorizontalLayout,
    SimpleButton,
    FormWrapper,
    CollapsibleContent, FormSelectInput, FormTextInput, FilterForm, DataTable, BasicCard, SimpleLayout
  },
  data: function () {
    return {
      query: {parameters: {}},
      processQuery: "",
      processQueryName: "",
      internalSort: {sortField: "startTime", direction: 0},
      focusedProcess: null,
      statusUpdatesTableConfig: {
        allowEdit: false,
        columns: [
          {
            caption: "Timestamp",
            datum: d => this.$formats.formatDateTime(d.timestamp, false)
          },
          {
            caption: "Progress",
            datum: d => `${d.progress * 100}%`
          },
          {
            caption: "Message",
            datum: d => d.message
          }
        ]
      },
      importProcessTableConfig: {
        allowEdit: true,
        columns: [
          {
            caption: "Id",
            datum: d => d.id.substr(d.id.length - 5)
          },
          {
            caption: "Progress",
            datum: d => this.$formats.formatNumber(Math.round((d?.progress ?? 0) * 100), "0") + "%",
            style: d => ({
              background: 'linear-gradient(90deg, #C84A4B ' + this.$formats.formatNumber(Math.round((d?.progress ?? 0) * 100), "0") + "%" + ', #00000000 ' + this.$formats.formatNumber(Math.round((d?.progress ?? 0) * 100), "0") + "%" + ')',
              color: 'white'
            })
          },
          {
            caption: "Job definition",
            datum: d => this.importDefinition(d.definitionId) == null ? "N/A" : this.importDefinition(d.definitionId).displayName
          },
          {
            caption: "Started by",
            datum: d => this.user(d.startedBy)?.username ?? `User[${d.startedBy}]`
          },
          // {
          //     caption: "Source",
          //     datum: d => d.values.source == undefined ? 'N/A' : d.values.source
          // },
          // {
          //     caption: "File",
          //     datum: d => d.values.file
          // },
          {
            caption: "Last message",
            datum: d => d.lastMessage,
            style: d => ({
              'white-space': 'normal'
            })
          },
          {
            caption: "Start timestamp",
            datum: d => this.$formats.formatDateTime(d.startTime, false)
          },
          {
            caption: "Finish timestamp",
            datum: d => d.finishTime != null ? this.$formats.formatDateTime(d.finishTime, false) : "-"
          }
        ]
      }
    }
  },
  methods: {
    clearFilter() {
      this.query = {parameters: {}}
      this.pageChanged()
    },
    saveFilter() {
      let toSave = this.$_.cloneDeep(this.query);
      if (this.processQueryName == "") {
        return;
      }
      toSave.name = this.processQueryName;
      toSave.object = "Process"
      this.$store.dispatch("processQueries/saveProcessQuery", toSave)
          .then(() => {
            this.$notify({
              group: 'application',
              title: `Query ${toSave.name} saved`,
              text: `Query ${this.accountQueryName} was successfully saved`,
              type: "ok"
            });
          });
    },
    pageChanged(page) {
      this.$store.dispatch(`importProcesses/refreshImportProcessesPaged`, {
        sort: this.internalSort,
        page,
        query: this.query
      })
          .then(() => this.loading = false);
    },
    handleFocusedChanged(focused) {
      this.focusedProcess = focused;
    }
  },
  watch: {
    processQuery: {
      handler(newVal) {
        let loadedQuery = this.$store.getters[`processQueries/queryById`](newVal);
        this.query = loadedQuery;
        this.processQueryName = loadedQuery.name;
        this.pageChanged();
      }
    },
  },
  computed: {
    ...mapGetters({
      importProcesses: 'importProcesses/importProcesses',
      importDefinition: 'importDefinitions/importDefinition',
      processQueries: 'processQueries/processQueries',
      user: 'users/user'
    }),
    processQueryOptions() {
      return this.processQueries
          .filter(query => query.object == 'Process')
          .map(query => ({id: query.id, name: query.name == null ? "No-name" : query.name}))
    },
    processQueryDefinition() {
      return QueryUtils.getByType("Process", {currencies: [], accounts: []});
    },
    sortedProcesses() {
      return [...this.importProcesses].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
    },
    listPagination() {
      return this.$store.getters[`importProcesses/importProcessPagination`];
    }
  },
  async mounted() {
    await this.$store.dispatch("processQueries/refreshProcessQueries");
    this.$store.dispatch(`importProcesses/refreshImportProcessesPaged`, {
      sort: this.internalSort,
      page: 0,
      query: this.query
    })
        .then(() => this.loading = false);
  }

}
</script>

<style lang="scss" scoped>

</style>